import React, { useMemo } from 'react';
import { Check, Pencil, Trash2, RotateCcw, ArrowRight, Calendar } from 'lucide-react';

// ── Deterministic hash for gradient palette derivation. ──
function _hash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function _posterGradient(id) {
  const h = _hash(id || 'x');
  const hue1 = h % 360;
  const hue2 = (hue1 + 32 + (h % 40)) % 360;
  return `linear-gradient(135deg, hsl(${hue1} 62% 26%) 0%, hsl(${hue2} 68% 8%) 100%)`;
}

// Poster glyph — latin → initials; non-latin → first grapheme.
function _glyph(title) {
  if (!title) return '';
  const t = title.trim();
  // eslint-disable-next-line no-control-regex -- ASCII range detector (\x00-\x7F): non-ASCII → non-latin script.
  if (/[^\x00-\x7F]/.test(t)) return t.slice(0, 2);
  const words = t.split(/\s+/).filter(w => /[A-Za-z]/.test(w[0]));
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return (words[0]?.slice(0, 2) || t.slice(0, 2)).toUpperCase();
}

// ── Title / description sanitisers ──────────────────────────────
// Split "वापसी (Vaapasi)" → ["वापसी", "(Vaapasi)"]; "The Last Rehearsal" → [title, null]
// Also strips "(Demo)" suffix — the amber DEMO badge already conveys that.
function _splitTitle(raw) {
  if (!raw) return { main: '', translit: null };
  let t = raw.replace(/\s*\(Demo\)\s*$/i, '').trim();
  const m = t.match(/^(.+?)\s*(\([^)]+\))\s*$/);
  if (m) return { main: m[1].trim(), translit: m[2].trim() };
  return { main: t, translit: null };
}

// Strip a trailing " (English translation…)" parenthetical from a non-latin logline.
// Heuristic: if the sentence contains non-ASCII, drop any trailing "(…)" that starts with a latin letter.
function _cleanLogline(raw) {
  if (!raw) return '';
  const t = raw.trim();
  // eslint-disable-next-line no-control-regex -- ASCII range detector; see _glyph above.
  if (!/[^\x00-\x7F]/.test(t)) return t;                    // pure ascii → untouched
  return t.replace(/\s*\(\s*[A-Za-z][^)]*\)\s*\.?\s*$/, '').trim();
}

const DEMO_LANG_LABEL = { english: 'English', hindi: 'हिन्दी', malayalam: 'മലയാളം' };

export default function ProjectCard({
  project, user, viewMode, selectedIds, renamingId, renameText,
  setRenameText, setRenamingId,
  navigate, toggleSelect,
  handleRestore, handleStartRename, handleDeleteProject, handleSaveRename,
  hasSelection = false,     // parent-driven: true when any card in the grid is selected → keep checkbox visible
}) {
  const gradient = useMemo(() => _posterGradient(project.id), [project.id]);
  const glyph = useMemo(() => _glyph(project.title), [project.title]);
  const { main: titleMain, translit } = useMemo(() => _splitTitle(project.title), [project.title]);
  const logline = useMemo(() => _cleanLogline(project.logline), [project.logline]);
  const isSelected = selectedIds.includes(project.id);
  const canManage = user?.role === 'ADMIN' || project.created_by === user?.id;
  const langLabel = project.is_demo && project.demo_key ? DEMO_LANG_LABEL[project.demo_key] : null;

  const checkboxVisible = isSelected || hasSelection;    // baseline visibility; hover-reveal handled by CSS

  return (
    <div
      className="fp-card group/proj relative flex flex-col cursor-pointer overflow-hidden focus-within:ring-2 focus-within:ring-offset-2"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') navigate(`/projects/${project.id}/breakdown`); }}
      onClick={() => navigate(`/projects/${project.id}/breakdown`)}
      role="link"
      aria-label={`Open ${titleMain}`}
      data-testid={`project-card-${project.id}`}
    >
      {/* ── Poster ─────────────────────────────────────────────── */}
      <div className="fp-poster relative overflow-hidden" style={{ height: 132 }}>
        <div className="absolute inset-0" style={{ background: gradient }} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.4,
            backgroundImage: 'radial-gradient(rgba(255,255,255,.12) 1px, transparent 1px)',
            backgroundSize: '14px 14px',
          }}
        />
        {/* Crisp filmstrip perforation — spec item #6 */}
        <div
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{
            height: 14,
            background: 'repeating-linear-gradient(90deg, transparent 0 7px, rgba(0,0,0,.7) 7px 11px)',
            opacity: 0.55,
          }}
        />
        <div
          className="absolute pointer-events-none select-none"
          style={{
            right: 14, bottom: 12,
            fontSize: 44, fontWeight: 800, letterSpacing: -2,
            color: 'rgba(255,255,255,.15)',
            lineHeight: 1,
          }}
        >
          {glyph}
        </div>

        {/* Badges — top-left, inset 12px */}
        <div className="absolute flex gap-1.5 items-center" style={{ top: 26, left: 12 }}>
          <span className="fp-badge fp-badge-active">{project.status === 'archived' ? 'Archived' : 'Active'}</span>
          {project.is_demo && (
            <span className="fp-badge fp-badge-demo" data-testid={`demo-badge-${project.id}`}>Demo</span>
          )}
          {langLabel && (
            <span
              className="fp-badge fp-badge-lang"
              data-testid={`demo-lang-badge-${project.demo_key}-${project.id}`}
              title={`Language: ${langLabel}`}
            >
              {langLabel}
            </span>
          )}
        </div>

        {/* Selection checkbox — small, hover-reveal, top-left inset 12px */}
        <label
          className={`fp-card-select ${checkboxVisible ? 'fp-card-select-active' : ''}`}
          style={{
            position: 'absolute', top: 6, left: 12, zIndex: 20,
            height: 18, width: 18, borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: isSelected ? 'var(--fp-amber)' : 'rgba(10,10,10,.55)',
            border: '1px solid ' + (isSelected ? 'var(--fp-amber)' : 'var(--fp-line-2)'),
            cursor: 'pointer',
          }}
          onClick={e => e.stopPropagation()}
          data-testid={`select-checkbox-${project.id}`}
          title={isSelected ? 'Deselect' : 'Select'}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={isSelected}
            onChange={e => toggleSelect(e, project.id)}
            aria-label={`${isSelected ? 'Deselect' : 'Select'} ${titleMain}`}
          />
          {isSelected && <Check className="h-3 w-3 text-black" strokeWidth={3.5} />}
        </label>

        {/* Manage buttons — top-right, hover-reveal */}
        {canManage && (
          <div
            className="absolute z-20 flex gap-1 opacity-0 group-hover/proj:opacity-100 transition-opacity duration-150"
            style={{ top: 8, right: 8 }}
          >
            {viewMode === 'archived' && (
              <button
                onClick={e => handleRestore(e, project)}
                className="fp-mng-btn"
                data-testid={`restore-project-${project.id}`}
                title="Restore"
                aria-label={`Restore ${titleMain}`}
              >
                <RotateCcw className="h-3 w-3" strokeWidth={2} />
              </button>
            )}
            <button
              onClick={e => handleStartRename(e, project)}
              className="fp-mng-btn"
              data-testid={`rename-project-${project.id}`}
              title="Rename"
              aria-label={`Rename ${titleMain}`}
            >
              <Pencil className="h-3 w-3" strokeWidth={2} />
            </button>
            <button
              onClick={e => handleDeleteProject(e, project)}
              className="fp-mng-btn fp-mng-btn-danger"
              data-testid={`delete-project-${project.id}`}
              title="Delete"
              aria-label={`Delete ${titleMain}`}
            >
              <Trash2 className="h-3 w-3" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1" style={{ padding: '16px 17px 17px' }}>
        {renamingId === project.id ? (
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <input
              autoFocus
              value={renameText}
              onChange={e => setRenameText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveRename(e, project); if (e.key === 'Escape') setRenamingId(null); }}
              className="text-base font-bold bg-transparent outline-none flex-1"
              style={{ color: 'var(--fp-txt)', borderBottom: '1px solid var(--fp-amber)' }}
              data-testid={`rename-input-${project.id}`}
              aria-label={`New title for ${titleMain}`}
            />
            <button
              onClick={e => handleSaveRename(e, project)}
              className="h-6 w-6 rounded flex items-center justify-center text-black"
              style={{ background: 'var(--fp-amber)' }}
              data-testid={`rename-save-${project.id}`}
              aria-label="Save rename"
            >
              <Check className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <h3
            className="tracking-tight"
            style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.3px', color: 'var(--fp-txt)' }}
          >
            {titleMain}
            {translit && (
              <span
                style={{ color: 'var(--fp-txt-3)', fontWeight: 500, fontSize: 14, marginLeft: 6 }}
              >
                {translit}
              </span>
            )}
          </h3>
        )}

        {project.production_company && (
          <div style={{ fontSize: 12, color: 'var(--fp-txt-3)', marginTop: 3 }}>
            {project.production_company}
          </div>
        )}

        {project.genre && (
          <span
            className="fp-genre-pill"
            style={{ marginTop: 10 }}
          >
            {project.genre}
          </span>
        )}

        {logline && (
          <p
            className="fp-card-desc"
            style={{ marginTop: 10 }}
            title={logline}
          >
            {logline}
          </p>
        )}

        <div
          className="flex items-center justify-between"
          style={{ marginTop: 14, paddingTop: 13, borderTop: '1px solid var(--fp-line)' }}
        >
          <div
            className="flex items-center gap-1.5"
            style={{ fontSize: 11.5, color: 'var(--fp-txt-3)' }}
          >
            <Calendar className="h-3 w-3" strokeWidth={2} />
            {new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          <div
            className="fp-open flex items-center gap-1"
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--fp-amber)' }}
          >
            Open
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          </div>
        </div>
      </div>
    </div>
  );
}
